import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Trash2, Calendar, Check, Clock, X, LayoutGrid, Table, ThumbsUp, ThumbsDown, Facebook, Instagram, Linkedin, MessageSquare, Twitter, Youtube, MessageCircle, Globe, Mail, Copy, Loader2, ExternalLink, Search } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Table as TableComponent, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StyledBadge } from "@/components/kol/StyledBadge";
import { format } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PicSelectorDialog } from "@/components/onboarding/PicSelectorDialog";
import { OnboardingForm } from "@/types/onboarding";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent, 
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { OnboardingFormsTable } from "@/components/onboarding/OnboardingFormsTable";
import { MultiSelect } from "@/components/ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OnboardingLinkManager } from "@/components/onboarding/OnboardingLinkManager";
import { PhaseStatus, PhaseStatusIndicator } from "@/components/onboarding/PhaseStatusIndicator";
import { OnboardingFormDialog } from "@/components/onboarding/OnboardingFormDialog";

export default function KolCompensation() {
  const [isLoading, setIsLoading] = useState(false);
  const [onboardingForms, setOnboardingForms] = useState<OnboardingForm[]>([]);
  const [formToDelete, setFormToDelete] = useState<string | null>(null);
  const [formToApprove, setFormToApprove] = useState<{id: string, approve: boolean} | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | "pending" | "completed" | "approved" | "declined" | "payment-sent" | "payment-confirmed">("all");
  const [viewMode, setViewMode] = useState<"cards" | "table">("table");
  const [picSelectorOpen, setPicSelectorOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<OnboardingForm | null>(null);
  const [processingApproval, setProcessingApproval] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const { hasFullAccess, user, userName } = useAuth();
  const [picOptions, setPicOptions] = useState<{value: string, label: string}[]>([]);
  const [isProcessingUpdate, setIsProcessingUpdate] = useState(false);
  
  const generalOnboardingLink = `${window.location.origin}/onboarding`;

  useEffect(() => {
    fetchOnboardingForms();
    fetchPICOptions();
  }, [hasFullAccess]);

  const fetchPICOptions = async () => {
    try {
      const { data: picData, error: picError } = await supabase
        .from('kol_pic')
        .select('id, name')
        .order('name');
      
      if (picError) throw picError;
      
      if (picData) {
        const options = picData.map(pic => ({
          value: pic.name,
          label: pic.name
        }));
        
        setPicOptions(options);
      }
    } catch (error) {
      console.error('Error fetching PIC options:', error);
    }
  };

  const fetchOnboardingForms = async () => {
    try {
      setIsLoading(true);
      
      let query = supabase
        .from('onboarding_forms')
        .select('*, kol_db:kol_db_id(*), pic_user:pic_id(*)')
        .order('created_at', { ascending: false });
      
      if (!hasFullAccess && user) {
        // Filter by either full access or forms where user is listed in the pic array
        query = query.or(`pic.cs.{${userName}},pic_id.eq.${user.id}`);
      }
      
      const { data, error } = await query;

      if (error) throw error;

      if (data) {
        setOnboardingForms(data as OnboardingForm[]);
      }
    } catch (error) {
      console.error("Error fetching forms:", error);
      toast({
        title: "Failed to load forms",
        description: "There was an error loading the applications",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteForm = async (formId: string) => {
    try {
      const { error } = await supabase
        .from('onboarding_forms')
        .delete()
        .eq('id', formId);

      if (error) throw error;

      toast({
        title: "Form deleted successfully",
        description: "The application has been removed",
      });
      fetchOnboardingForms();
    } catch (error) {
      console.error("Error deleting form:", error);
      toast({
        title: "Failed to delete form",
        description: "There was an error removing the application",
        variant: "destructive",
      });
    } finally {
      setFormToDelete(null);
    }
  };

  const handleApprovalClick = (form: OnboardingForm) => {
    setSelectedForm(form);
    setPicSelectorOpen(true);
  };

  const handleDecline = async (formId: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('onboarding_forms')
        .update({ 
          approved: 'declined', 
          status: 'declined' 
        })
        .eq('id', formId);

      if (error) throw error;
      
      toast({
        title: "Form declined",
        description: "The application has been declined",
      });
      
      fetchOnboardingForms();
    } catch (error) {
      console.error("Error declining form:", error);
      toast({
        title: "Error declining form",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setFormToApprove(null);
    }
  };

  const handlePicSave = async (picArray: string[], picId: string | null, campaignBU: string) => {
    if (selectedForm) {
      setProcessingApproval(true);
      setShowProgressDialog(true);
      
      try {
        // Update the form with pic and campaign_bu
        setProcessingProgress(25);
        
        const { error } = await supabase
          .from('onboarding_forms')
          .update({ 
            pic: picArray,
            pic_id: picId,  // Also update pic_id
            approved: 'approved',
            status_phase_2: 'completed',
            status_phase_3: 'in-progress',
            campaign_bu: campaignBU // Save the campaign_bu
          })
          .eq('id', selectedForm.id);
          
        if (error) throw error;
        
        setProcessingProgress(100);
        
        toast({
          title: "Approval successful",
          description: "The creator has been assigned PIC managers and approved",
        });
        
        fetchOnboardingForms();
      } catch (error) {
        console.error("Error during approval process:", error);
        toast({
          title: "Error during approval",
          description: "There was an issue during the approval process",
          variant: "destructive",
        });
      } finally {
        setProcessingApproval(false);
        setShowProgressDialog(false);
        setPicSelectorOpen(false);
        setSelectedForm(null);
      }
    }
  };

  const filteredForms = onboardingForms
    .filter(form => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = form.kol_name?.toLowerCase().includes(query) || false;
        const matchesHandler = form.social_media_handler?.toLowerCase().includes(query) || false;
        const matchesRegion = form.region?.toLowerCase().includes(query) || false;
        const matchesEmail = form.email?.toLowerCase().includes(query) || false;
        const matchesFeedback = form.feedback?.toLowerCase().includes(query) || false;
        const matchesDeliverables = form.deliverables?.toLowerCase().includes(query) || false;
        const matchesDescription = form.description?.toLowerCase().includes(query) || false;
        const matchesCurrencyType = form.currency_type?.toLowerCase().includes(query) || false;
        
        if (!(matchesName || matchesHandler || matchesRegion || matchesEmail || 
             matchesFeedback || matchesDeliverables || matchesDescription || 
             matchesCurrencyType)) {
          return false;
        }
      }
      
      if (filterStatus === "approved" && form.approved !== 'approved') {
        return false;
      } else if (filterStatus === "declined" && form.approved !== 'declined') {
        return false;
      } else if (filterStatus === "pending" && form.approved !== 'pending') {
        return false;
      } else if (filterStatus === "completed" && form.status !== "completed") {
        return false;
      } else if (filterStatus === "payment-sent" && form.payment_status !== "Payment Sent") {
        return false;
      } else if (filterStatus === "payment-confirmed" && form.payment_status !== "Payment Confirmed") {
        return false;
      } else if (filterStatus !== "all") {
        return false;
      }
      
      return true;
    });

  const getStatusBadgeClass = (status?: string, approved?: string, paymentStatus?: string): string => {
    if (paymentStatus === 'Payment Confirmed') return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    if (paymentStatus === 'Payment Sent') return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    if (approved === 'approved') return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    if (approved === 'declined') return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    if (status === 'completed') return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
  };

  const getStatusLabel = (status?: string, approved?: string, paymentStatus?: string): string => {
    if (paymentStatus === 'Payment Confirmed') return "Payment Confirmed";
    if (paymentStatus === 'Payment Sent') return "Payment Sent";
    if (approved === 'approved') return "Approved";
    if (approved === 'declined') return "Declined";
    if (status === 'completed') return "Completed";
    return "Pending";
  };

  const handleFormUpdate = async (formId: string, updatedData: Partial<OnboardingForm>): Promise<boolean> => {
    setIsProcessingUpdate(true);
    try {
      // Special handling for pic array updates
      if (updatedData.pic !== undefined && Array.isArray(updatedData.pic)) {
        // If there's only one PIC in array, we'll update pic_id field as well
        if (updatedData.pic.length === 1) {
          try {
            // First try to find the PIC's id from the name
            const { data: picData, error: picError } = await supabase
              .from('kol_pic')
              .select('id')
              .eq('name', updatedData.pic[0])
              .single();
              
            if (!picError && picData) {
              // Found a matching PIC, update pic_id field
              updatedData.pic_id = picData.id;
            }
          } catch (err) {
            console.error('Error finding PIC id:', err);
            // Continue with update even if we couldn't find PIC id
          }
        } else {
          // Multiple PICs or no PICs, set pic_id to null
          updatedData.pic_id = null;
        }
      }
      
      // Regular update
      const { error } = await supabase
        .from('onboarding_forms')
        .update(updatedData)
        .eq('id', formId);
      
      if (error) throw error;
      
      toast({
        title: "Form updated successfully",
        description: "The form has been updated",
      });
      
      // Update the local state
      setOnboardingForms(forms => 
        forms.map(form => form.id === formId ? { ...form, ...updatedData } : form)
      );
      
      return true;
    } catch (error) {
      console.error('Error updating form:', error);
      toast({
        title: "Failed to update form",
        description: "There was an error updating the form",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsProcessingUpdate(false);
    }
  };

  const getPhaseStatus = (status?: string): PhaseStatus => {
    if (!status) return "pending";
    switch (status) {
      case "completed": return "completed";
      case "in-progress": return "in-progress";
      case "error": return "error";
      default: return "pending";
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="dashboard-card border-gray-300 dark:border-gray-700">
        <Accordion type="single" defaultValue="submitted-forms" collapsible>
          <AccordionItem value="submitted-forms">
            <AccordionTrigger className="px-6 border-b border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200">
              Global Content Creator Application Form ({filteredForms.length})
            </AccordionTrigger>
            <AccordionContent>
              <div className="px-6 py-2 border-b border-gray-300 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="h-8 border-blue-500 text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-900/30"
                      onClick={fetchOnboardingForms}
                      disabled={isLoading}
                    >
                      <RefreshCw className={`mr-1 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    
                    <div className="relative">
                      <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
                      <Input 
                        placeholder="Search..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bg-white h-8 dark:bg-transparent border-gray-300 dark:border-gray-700 pl-8 w-64"
                      />
                    </div>
                    
                    <Select value={filterStatus} onValueChange={(value: any) => setFilterStatus(value)}>
                      <SelectTrigger className="h-8 w-[160px]">
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="declined">Declined</SelectItem>
                        <SelectItem value="payment-sent">Payment Sent</SelectItem>
                        <SelectItem value="payment-confirmed">Payment Confirmed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* New Generate Onboarding Form button */}
                  <div>
                    <OnboardingFormDialog />
                  </div>
                </div>
              </div>
              <CardContent className="p-6">
                {isLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <p className="text-gray-700 dark:text-gray-300">Loading forms...</p>
                  </div>
                ) : filteredForms.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <div className="h-16 w-16 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                      <Table className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500">No forms {searchQuery ? "found matching your search" : "submitted yet"}</p>
                    {searchQuery ? (
                      <p className="text-gray-500 text-sm mt-2">Try a different search term</p>
                    ) : (
                      <p className="text-gray-500 text-sm mt-2">Share the onboarding form link to get creators to submit their details</p>
                    )}
                  </div>
                ) : viewMode === "table" ? (
                  <OnboardingFormsTable 
                    forms={filteredForms}
                    isLoading={isLoading}
                    onApprove={(formId) => {
                      const form = filteredForms.find(f => f.id === formId);
                      if (form) handleApprovalClick(form);
                    }}
                    onDecline={handleDecline}
                    searchQuery={searchQuery}
                    statusFilter={filterStatus}
                    onUpdateForm={handleFormUpdate}
                    picOptions={picOptions}
                    isUserRegionalManager={hasFullAccess}
                  />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                    {filteredForms.map((form) => (
                      <Card key={form.id} className="hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.03)] transition-colors border border-gray-300 dark:border-gray-700 overflow-hidden">
                        <CardContent className="p-2">
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1">
                                <h3 className="font-medium text-xs text-gray-800 dark:text-gray-200 truncate max-w-[140px]">{form.kol_name}</h3>
                                {form.social_media_type && (
                                  <StyledBadge 
                                    value={form.social_media_type} 
                                    variant="purple" 
                                    extraSmall 
                                  />
                                )}
                              </div>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getStatusBadgeClass(form.status, form.approved, form.payment_status)}`}>
                                {getStatusLabel(form.status, form.approved, form.payment_status)}
                              </span>
                            </div>
                            
                            <div className="flex flex-wrap gap-1">
                              {form.social_media_handler && (
                                <span className="text-blue-600 dark:text-blue-400">@{form.social_media_handler}</span>
                              )}
                              
                              {form.region && (
                                <StyledBadge 
                                  value={form.region} 
                                  variant="blue" 
                                  extraSmall
                                />
                              )}

                              {form.deliverables && (
                                <span className="text-gray-600 truncate max-w-[140px]">{form.deliverables}</span>
                              )}
                            </div>

                            <div className="mt-2 flex flex-wrap gap-1">
                              {[1, 2, 3, 4, 5, 6, 7].map((phaseNum) => {
                                const phaseKey = `status_phase_${phaseNum}` as keyof OnboardingForm;
                                const phaseStatus = getPhaseStatus(form[phaseKey] as string);
                                return (
                                  <PhaseStatusIndicator
                                    key={phaseNum}
                                    status={phaseStatus}
                                    phase={phaseNum}
                                    className="scale-75 origin-left"
                                  />
                                );
                              })}
                            </div>
                          </div>
                          <OnboardingLinkManager form={form} />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>

      {/* PIC Selector Dialog */}
      {selectedForm && (
        <PicSelectorDialog
          open={picSelectorOpen}
          onClose={() => setPicSelectorOpen(false)}
          form={selectedForm}
          onSave={handlePicSave}
        />
      )}

      {/* Progress Dialog */}
      <Dialog open={showProgressDialog} onOpenChange={setShowProgressDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Processing Approval</DialogTitle>
            <DialogDescription>
              Setting up the necessary records...
            </DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
              <div 
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${processingProgress}%` }} 
              ></div>
            </div>
            <div className="text-center mt-2 text-sm text-gray-500">
              {processingProgress < 100 ? 'Processing...' : 'Completed!'}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
